import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

export type AppNotification = {
  id: string;
  user_id: string;
  message: string;
  severity: 'minimum' | 'medium' | 'severe';
  viewed: 'yes' | 'no';
  created_at: string;
};

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['app_notifications'],
    queryFn: async () => {
      const { data: authData } = await supabase.auth.getSession();
      if (!authData.session) return [];

      const { data, error } = await supabase
        .from('app_notifications')
        .select('*')
        .eq('viewed', 'no')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error("Error fetching notifications:", error);
        return [];
      }
      return data as AppNotification[];
    },
    refetchInterval: 120000, // Poll every 2 minutes
    refetchIntervalInBackground: true, // Continue polling even if window loses focus
  });

  const markAsViewed = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('app_notifications')
        .update({ viewed: 'yes' })
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['app_notifications'] });
      
      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData<AppNotification[]>(['app_notifications']);
      
      // Optimistically update to the new value by removing the viewed notification
      if (previousNotifications) {
        queryClient.setQueryData<AppNotification[]>(
          ['app_notifications'],
          previousNotifications.filter(n => n.id !== id)
        );
      }
      
      return { previousNotifications };
    },
    onError: (err, newTodo, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotifications) {
        queryClient.setQueryData(['app_notifications'], context.previousNotifications);
      }
      toast({
        title: "Error",
        description: "Failed to mark notification as viewed.",
        variant: "destructive"
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['app_notifications'] });
    }
  });

  return {
    notifications,
    isLoading,
    markAsViewed: markAsViewed.mutate,
  };
};
